import { memo, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import {
  addSnippet,
  folder,
  folders,
  initialSnippetSettings,
  snippetsTabsValue,
  usage,
} from "rtk-slice"

import { isMobile } from "utils/mediaQuery"

import {
  Backdrop,
  SpeedDial,
  SpeedDialAction,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material"

import {
  ColorsCircles3,
  Cross,
  InfoSolid,
  MenuColored,
  SettingsCircle,
} from "ui-icons"
import { v4 as uuidv4 } from "uuid"

import { setUsageToFirebase } from "../features/firebase/firebaseFunctions"
import { router } from "../routes/router"

const FloatingMenu = () => {
  const dispatch = useDispatch()
  const { snippetsQty } = useSelector(usage)
  const initialSnippetSettingsState = useSelector(
    initialSnippetSettings
  )

  const folderState = useSelector(folder)
  const foldersState = useSelector(folders)
  const snippetsTabsValueState = useSelector(snippetsTabsValue)

  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const newSnippetId = uuidv4()
  const {
    palette: {
      primary: { main },
      mode,
    },
  } = useTheme()

  const actions = [
    {
      name: "Add Snippet",
      icon: <Cross fill={main} width="30" height="30" />,
      onclick: handleAddNewSnippet,
    },
    {
      name: "Colors",
      icon: <ColorsCircles3 />,
      to: "/palette",
    },
    {
      name: "Info",
      icon: (
        <Typography
          variant="h4"
          sx={{
            "& svg circle": { fill: "red" },
            "& svg path:first-of-type": {
              fill: mode === "dark" ? "white" : "#212121",
            },
          }}
          className="Text"
        >
          <InfoSolid />
        </Typography>
      ),
      to: "/info",
    },
    {
      name: "Settings",
      icon: <SettingsCircle width="30" height="30" fill={main} />,
      to: "/settings",
    },
  ]

  const handleNavigate = (route) => {
    router.navigate(`${route + window.location.search}`, {
      state: { prevPath: window.location.pathname },
      replace: false,
    })
  }

  const isFolderOpened = useMemo(
    () => foldersState.find((folder) => folder.title === folderState),
    [folderState, foldersState]
  )

  function handleAddNewSnippet() {
    try {
      dispatch(
        addSnippet({
          id: newSnippetId,
          title: "Untitled Snippet",
          body: null,
          createdAt: new Date().toISOString(),
          flagged: false,
          starred: false,
          isPinned: snippetsTabsValueState ? false : true,
          isSnippetLocked: false,
          folder: folderState ? isFolderOpened : null,
          lastEdit: "",
          bookmarkColor: "",
          type: null,
          descriptions: [],
          settings: initialSnippetSettingsState,
        })
      )
      setUsageToFirebase({
        document: "snippets",
        value: snippetsQty + 1,
        dispatch,
      })
      handleNavigate(
        `${
          window.location.pathname !== "/snippets" ? "snippets/" : ""
        }edit/${newSnippetId.slice(0, 8)}`
      )
    } catch (error) {
      throw new Error(error)
    }
  }

  return (
    <div>
      <Toolbar
        sx={{
          right: 80,
          bottom: 30,
          position: "fixed",
          width: "0px",
          padding: "0px !important",
          zIndex: 1000,
        }}
      >
        {isMobile() ? (
          <Backdrop
            open={open}
            sx={{
              background: "hsl(30% 70% 10% / 0.6)",
              backdropFilter: "blur(5px)",
            }}
          />
        ) : null}
        <SpeedDial
          ariaLabel="Main menu"
          icon={<MenuColored width="32" height="32" stroke={main} />}
          onClose={isMobile() ? handleClose : null}
          onOpen={handleOpen}
          open={open}
          sx={{
            "& > .MuiButtonBase-root:first-of-type": {
              bgcolor: "#555",
              width: "4em",
              height: "4em",
            },
          }}
        >
          {actions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              tooltipOpen={isMobile() ? true : false}
              onClick={() => {
                handleClose()
                action.to ? handleNavigate(action.to) : null
                action.onclick ? action.onclick() : null
              }}
              aria-label="menu item"
            />
          ))}
        </SpeedDial>
      </Toolbar>
    </div>
  )
}

export default memo(FloatingMenu)
